import type { TranslationNode } from '~/utils/translationSession'
import { httpsCallable } from 'firebase/functions'
import { defineStore } from 'pinia'
import { functionsAsia } from '~~/firebaseConfig'
import { SITE_LANGUAGES } from '~/utils/siteLanguages'
import { createTranslationSession, splitTranslationText } from '~/utils/translationSession'

const STORAGE_KEY = 'oo-ing-site-translate-lang'
const SOURCE_LANG = 'ko'

export const useSiteTranslationStore = defineStore('siteTranslation', () => {
  const currentLang = ref(SOURCE_LANG)
  const isTranslating = ref(false)
  const errorMessage = ref('')
  const { setLocale } = useI18n()
  const { getInitialLocale, savePreferredLocale } = useLocalePreference()
  const session = createTranslationSession()
  let initialized = false
  const observers = new Map<HTMLElement | ShadowRoot, MutationObserver>()
  const frameListeners = new Map<HTMLIFrameElement, () => void>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let busyRevision: number | null = null
  let rerun = false
  const attributeNodes = new Map<Element, Map<string, TranslationNode>>()
  const excluded = 'script, style, noscript, .no-translate, [translate="no"]'
  const editable = 'input, textarea, [contenteditable]:not([contenteditable="false"])'
  const translatedAttributes = ['placeholder', 'title', 'aria-label', 'alt', 'data-placeholder']

  function collectRoots() {
    const roots = new Set<HTMLElement | ShadowRoot>()
    const frames = new Set<HTMLIFrameElement>()
    function visit(root: HTMLElement | ShadowRoot) {
      if (roots.has(root))
        return
      roots.add(root)
      for (const element of root.querySelectorAll('*')) {
        if (element.closest(excluded))
          continue
        if (element.shadowRoot)
          visit(element.shadowRoot)
        if (element.tagName === 'IFRAME') {
          const frame = element as HTMLIFrameElement
          frames.add(frame)
          if (!frameListeners.has(frame)) {
            frame.addEventListener('load', schedule)
            frameListeners.set(frame, schedule)
          }
          // Cross-origin frames deliberately return null; no access restrictions are bypassed.
          if (frame.contentDocument?.body)
            visit(frame.contentDocument.body)
        }
      }
    }
    visit(document.body)
    for (const [root, observer] of observers) {
      if (!roots.has(root)) {
        observer.disconnect()
        observers.delete(root)
      }
    }
    for (const root of roots) {
      if (!observers.has(root)) {
        const observer = new MutationObserver(schedule)
        observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [...translatedAttributes, 'value'],
        })
        observers.set(root, observer)
      }
    }
    for (const [frame, listener] of frameListeners) {
      if (!frames.has(frame)) {
        frame.removeEventListener('load', listener)
        frameListeners.delete(frame)
      }
    }
    return roots
  }

  function collectNodes() {
    const nodes: TranslationNode[] = []
    for (const element of attributeNodes.keys()) {
      if (!element.isConnected)
        attributeNodes.delete(element)
    }
    for (const root of collectRoots()) {
      const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!/\p{L}/u.test(node.textContent || '') || node.parentElement?.closest(`${excluded}, ${editable}`))
            return NodeFilter.FILTER_REJECT
          return NodeFilter.FILTER_ACCEPT
        },
      })
      let node = walker.nextNode()
      while (node) {
        nodes.push(node as Text)
        node = walker.nextNode()
      }
      const selector = translatedAttributes.map(attribute => `[${attribute}]`).join(',')
      for (const element of root.querySelectorAll(`${selector}, input[type="button"], input[type="submit"], input[type="reset"]`)) {
        if (element.closest(excluded))
          continue
        const names = element.matches('input[type="button"], input[type="submit"], input[type="reset"]')
          ? [...translatedAttributes, 'value']
          : translatedAttributes
        for (const attribute of names) {
          const value = element.getAttribute(attribute)
          if (!value || !/\p{L}/u.test(value))
            continue
          let attributes = attributeNodes.get(element)
          if (!attributes) {
            attributes = new Map()
            attributeNodes.set(element, attributes)
          }
          let target = attributes.get(attribute)
          if (!target) {
            target = {
              get textContent() { return element.getAttribute(attribute) },
              set textContent(text) { element.setAttribute(attribute, text || '') },
              get isConnected() { return element.isConnected },
            }
            attributes.set(attribute, target)
          }
          nodes.push(target)
        }
      }
    }
    return nodes
  }

  function schedule() {
    if (timer)
      clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      void translatePage()
    }, 600)
  }

  async function translatePage() {
    if (currentLang.value === SOURCE_LANG || errorMessage.value)
      return
    const revision = session.revision
    if (busyRevision === revision) {
      rerun = true
      return
    }
    const batch = session.prepare(collectNodes())
    if (!batch.length)
      return
    busyRevision = revision
    rerun = false
    isTranslating.value = true
    const targetLang = currentLang.value
    try {
      const call = httpsCallable<
        { texts: string[], targetLang: string, sourceLang: string, cache: boolean },
        { ok: boolean, translations: string[] }
      >(functionsAsia, 'translateSiteText', { timeout: 30000 })
      const segments = batch.map(item => splitTranslationText(item.source))
      const pending = segments.flatMap(parts => parts.map((text, index) => ({ parts, text, index })))
        .filter(item => /\p{L}/u.test(item.text))
      for (let start = 0; start < pending.length; start += 500) {
        const chunk = pending.slice(start, start + 500)
        // DOM content can include private conversations: never use the shared Firestore cache.
        const { data } = await call({ texts: chunk.map(item => item.text.trim()), targetLang, sourceLang: 'auto', cache: false })
        if (revision !== session.revision)
          return
        if (!data.ok || !Array.isArray(data.translations) || data.translations.length !== chunk.length
          || data.translations.some(text => typeof text !== 'string' || !text.trim())) {
          throw new Error('Invalid translation response.')
        }
        chunk.forEach((item, index) => {
          item.parts[item.index] = item.text.replace(item.text.trim(), () => data.translations[index]!.trim())
        })
      }
      session.apply(batch, segments.map(parts => parts.join('')))
    }
    catch (error) {
      if (revision === session.revision) {
        console.error('사이트 번역 실패:', error)
        errorMessage.value = 'Translation failed. Open the language menu and retry.'
        useSystemStore().showError(errorMessage.value)
      }
    }
    finally {
      if (revision === session.revision) {
        busyRevision = null
        isTranslating.value = false
        if (rerun && !errorMessage.value)
          schedule()
      }
    }
  }

  function stop() {
    for (const observer of observers.values())
      observer.disconnect()
    observers.clear()
    for (const [frame, listener] of frameListeners)
      frame.removeEventListener('load', listener)
    frameListeners.clear()
    if (timer)
      clearTimeout(timer)
    timer = null
    session.reset()
    attributeNodes.clear()
    busyRevision = null
    isTranslating.value = false
  }

  async function selectLanguage(code: string) {
    if (!SITE_LANGUAGES.some(language => language.code === code)) {
      console.error('지원하지 않는 번역 언어:', code)
      useSystemStore().showError('This language is not supported.')
      return
    }
    stop()
    const revision = session.revision
    currentLang.value = code
    errorMessage.value = ''
    try {
      // A single Korean source prevents mixed i18n/automatic translation originals.
      savePreferredLocale('ko')
      localStorage.setItem(STORAGE_KEY, code)
    }
    catch (error) {
      console.warn('언어 설정을 저장하지 못했습니다:', error)
    }
    await setLocale('ko')
    await nextTick()
    if (revision !== session.revision || code === SOURCE_LANG)
      return
    await translatePage()
  }

  async function initialize() {
    if (initialized)
      return
    initialized = true
    let saved: string | null = null
    const initialLocale = getInitialLocale()
    try {
      saved = localStorage.getItem(STORAGE_KEY)
    }
    catch (error) {
      console.warn('저장된 언어 설정을 읽지 못했습니다:', error)
    }
    await selectLanguage(saved && SITE_LANGUAGES.some(language => language.code === saved) ? saved : initialLocale)
  }

  return { currentLang, isTranslating, errorMessage, initialize, selectLanguage, stop }
})
